import React, { useState } from 'react';

interface Task {
  id: string;
  number: number;
  title: string;
  description: string;
  requirements: Array<{
    text: string;
    subitems?: string[];
  }>;
  verification: string[];
}

const tasks: Task[] = [
  {
    id: 'task-1',
    number: 1,
    title: 'Diagnose Application Stack Issues',
    description: 'Identify and understand all the issues preventing the React/Node.js application from running properly.',
    requirements: [
      {
        text: 'Check the status of all application services (frontend and backend)'
      },
      {
        text: 'Review application logs to identify crash reasons'
      },
      {
        text: 'Verify Node.js and npm versions meet requirements'
      },
      {
        text: 'Check if all required ports are available and not blocked'
      },
      {
        text: 'Document all issues found for systematic resolution'
      }
    ],
    verification: [
      'You have identified all service failures',
      'You understand why the application is not working',
      'You have checked Node.js and npm versions',
      'You have verified port availability',
      'You have a clear list of issues to fix'
    ]
  },
  {
    id: 'task-2',
    number: 2,
    title: 'Fix Backend API Service',
    description: 'Resolve issues preventing the Node.js backend API from starting and serving requests properly.',
    requirements: [
      {
        text: 'Navigate to the backend application directory (/home/ubuntu/app/backend)'
      },
      {
        text: 'Fix missing dependencies and install required packages'
      },
      {
        text: 'Correct environment configuration issues',
        subitems: ['Database connection settings', 'API port configuration', 'JWT secret configuration']
      },
      {
        text: 'Fix syntax errors in server.js (check for missing parentheses and typos like /helth vs /health)'
      },
      {
        text: 'Ensure the backend service starts and stays running'
      }
    ],
    verification: [
      'Backend dependencies are installed',
      'Environment variables are properly configured',
      'No syntax errors in backend code',
      'Backend API is running on port 5000',
      'API health endpoint responds correctly'
    ]
  },
  {
    id: 'task-3',
    number: 3,
    title: 'Repair Database Connection',
    description: 'Fix database connectivity issues preventing the backend from accessing PostgreSQL.',
    requirements: [
      {
        text: 'Verify PostgreSQL service is running'
      },
      {
        text: 'Create the required database and user if missing'
      },
      {
        text: 'Use database appdb, user appuser, and password apppass123'
      },
      {
        text: 'Fix database connection string in the backend configuration'
      },
      {
        text: 'Run database migrations to create the users and posts tables'
      },
      {
        text: 'Test database connectivity from the backend'
      }
    ],
    verification: [
      'PostgreSQL service is active and running',
      'Database appdb and user appuser exist with proper permissions',
      'Connection string is correctly formatted',
      'users and posts tables exist in appdb',
      'Backend can query the database'
    ]
  },
  {
    id: 'task-4',
    number: 4,
    title: 'Fix React Frontend Application',
    description: 'Resolve build and runtime issues in the React frontend application.',
    requirements: [
      {
        text: 'Navigate to the frontend directory (/home/ubuntu/app/frontend)'
      },
      {
        text: 'Install missing frontend dependencies'
      },
      {
        text: 'Fix webpack configuration errors'
      },
      {
        text: 'Correct API endpoint configuration to connect to backend'
      },
      {
        text: 'Build the production bundle successfully to /home/ubuntu/app/frontend/dist'
      }
    ],
    verification: [
      'All frontend dependencies are installed',
      'Webpack configuration is valid',
      'API endpoints point to correct backend URL',
      'Production build completes without errors',
      'Built files exist in the dist directory (index.html and bundle.js)'
    ]
  },
  {
    id: 'task-5',
    number: 5,
    title: 'Configure Nginx Reverse Proxy',
    description: 'Set up Nginx to properly serve the React app and proxy API requests to the backend.',
    requirements: [
      {
        text: 'Install and enable Nginx if not already installed'
      },
      {
        text: 'Configure Nginx to serve the React build files'
      },
      {
        text: 'Set up reverse proxy for /api routes to the backend'
      },
      {
        text: 'Required for grading:',
        subitems: [
          'Enable site named "app": /etc/nginx/sites-available/app -> /etc/nginx/sites-enabled/app',
          'Set root to /home/ubuntu/app/frontend/dist',
          'Set proxy_pass for API traffic to http://localhost:5000',
          'Ensure production build exists at /home/ubuntu/app/frontend/dist/bundle.js',
          'GET /api/health returns HTTP 200 through Nginx'
        ]
      },
      {
        text: 'Fix CORS headers for API requests'
      },
      {
        text: 'Enable and start the Nginx service'
      }
    ],
    verification: [
      'Nginx is installed and running',
      'React app is accessible on port 80',
      'API requests are properly proxied to backend',
      'CORS errors are resolved',
      'Static assets load correctly'
    ]
  },
  {
    id: 'task-6',
    number: 6,
    title: 'Set Up Process Management',
    description: 'Configure PM2 to keep the Node.js backend running persistently and handle automatic restarts.',
    requirements: [
      {
        text: 'Install PM2 globally using npm'
      },
      {
        text: 'Create PM2 ecosystem configuration file'
      },
      {
        text: 'Start the backend API with PM2 (name it "api-backend")'
      },
      {
        text: 'Save the PM2 process list so api-backend can restart after reboot'
      },
      {
        text: 'Verify PM2 monitoring and log management'
      }
    ],
    verification: [
      'PM2 is installed and accessible',
      'Backend is managed by PM2',
      'PM2 shows the backend process as online',
      'PM2 process list is saved',
      'Application restarts automatically on crash'
    ]
  },
  {
    id: 'task-7',
    number: 7,
    title: 'Implement Health Monitoring',
    description: 'Set up health check endpoints and monitoring to ensure the application stack is fully operational.',
    requirements: [
      {
        text: 'Create or fix the /health endpoint in the backend API'
      },
      {
        text: 'Implement database connectivity check in the health response'
      },
      {
        text: 'Add database, memory, and CPU fields to the health response'
      },
      {
        text: 'Ensure the proxied /api/health response includes database status'
      },
      {
        text: 'Test the complete application flow from frontend to database'
      }
    ],
    verification: [
      'Health endpoint returns 200 OK',
      'Health check includes database, memory, and CPU fields',
      'Proxied health check includes database status',
      'Full application stack is operational',
      'User can interact with the application successfully'
    ]
  }
];

const ReactNodeStackBreakdownInstructions: React.FC = () => {
  const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
  const [completedChecks, setCompletedChecks] = useState<Set<string>>(new Set());
  const [completedTasks, setCompletedTasks] = useState<Set<number>>(new Set());

  const handleTaskClick = (index: number) => {
    setCurrentTaskIndex(index);
  };

  const handlePrevious = () => {
    if (currentTaskIndex > 0) {
      setCurrentTaskIndex(currentTaskIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentTaskIndex < tasks.length - 1) {
      setCurrentTaskIndex(currentTaskIndex + 1);
    }
  };

  const toggleVerification = (taskId: string, checkIndex: number) => {
    const checkId = `${taskId}-check-${checkIndex}`;
    const newChecks = new Set(completedChecks);
    
    if (newChecks.has(checkId)) {
      newChecks.delete(checkId);
    } else {
      newChecks.add(checkId);
    }
    
    setCompletedChecks(newChecks);

    // Check if all verifications for current task are completed
    const allChecksCompleted = tasks[currentTaskIndex].verification.every(
      (_, idx) => newChecks.has(`${tasks[currentTaskIndex].id}-check-${idx}`)
    );

    if (allChecksCompleted && !completedTasks.has(currentTaskIndex)) {
      const newCompletedTasks = new Set(completedTasks);
      newCompletedTasks.add(currentTaskIndex);
      setCompletedTasks(newCompletedTasks);
    }
  };

  const currentTask = tasks[currentTaskIndex];
  const progressPercentage = ((currentTaskIndex + 1) / tasks.length) * 100;

  return (
    <div className="bg-[#232937] rounded-xl overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#10b981] to-[#059669] px-8 py-6">
        <h2 className="text-2xl font-semibold text-[#1a1f2e]">Instructions</h2>
      </div>

      {/* Content Area - Increased to 1800px for less scrolling */}
      <div className="flex h-[1800px]">
        {/* Sidebar Navigation */}
        <div className="w-[280px] bg-[#1a1f2e] border-r border-[#2d3748] overflow-y-auto">
          {tasks.map((task, index) => (
            <div
              key={task.id}
              onClick={() => handleTaskClick(index)}
              className={`flex items-center px-5 py-4 cursor-pointer border-b border-[#2d3748] hover:bg-[#2d3748] transition-all relative ${
                index === currentTaskIndex ? 'bg-[#2d3748] border-l-[3px] border-l-[#10b981] pl-[17px]' : ''
              }`}
            >
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold mr-3 flex-shrink-0 ${
                completedTasks.has(index) ? 'bg-[#059669]' : 'bg-[#10b981]'
              } text-[#1a1f2e]`}>
                {task.number}
              </div>
              <div className={`text-sm ${index === currentTaskIndex ? 'text-[#e2e8f0] font-medium' : 'text-[#cbd5e1]'}`}>
                {task.title}
              </div>
              {completedTasks.has(index) && (
                <span className="absolute right-5 text-[#10b981] font-bold text-sm">✓</span>
              )}
            </div>
          ))}
        </div>

        {/* Main Content */}
        <div className="flex-1 p-8 bg-[#1e293b] overflow-y-auto">
          <h3 className="text-[22px] font-semibold text-[#e2e8f0] mb-4">
            Task {currentTask.number}: {currentTask.title}
          </h3>
          <p className="text-[#94a3b8] mb-8 leading-[1.6] text-[15px]">
            {currentTask.description}
          </p>

          {/* Requirements Section */}
          <div className="mb-8">
            <h4 className="text-[#10b981] text-xs uppercase tracking-[1px] mb-4 font-semibold flex items-center">
              <span className="w-5 h-[2px] bg-[#10b981] mr-[10px]"></span>
              What You Need To Do
            </h4>
            
            {currentTask.requirements.map((requirement, reqIndex) => (
              <div key={reqIndex} className="bg-[#232937] border border-[#2d3748] rounded-lg p-4 mb-3 hover:border-[#374151] hover:shadow-md transition-all">
                <div className="text-[#e2e8f0] text-sm leading-[1.8]">
                  <span className="font-medium">{reqIndex + 1}. {requirement.text}</span>
                  {requirement.subitems && (
                    <ul className="mt-2 ml-5 space-y-1">
                      {requirement.subitems.map((item, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="text-[#10b981] mr-2 mt-0.5">•</span>
                          <span className="text-[#cbd5e1]">{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Verification Section */}
          <div className="bg-[#232937] border-2 border-dashed border-[#475569] rounded-lg p-5 mt-6">
            <div className="flex items-center text-[#10b981] font-semibold mb-4 text-base">
              <span className="w-5 h-5 bg-[#10b981] text-[#1a1f2e] rounded-full flex items-center justify-center text-xs mr-[10px]">
                ✓
              </span>
              Verification
            </div>
            
            {currentTask.verification.map((check, checkIndex) => (
              <div
                key={checkIndex}
                onClick={() => toggleVerification(currentTask.id, checkIndex)}
                className="flex items-center p-[10px_12px] bg-[#1a1f2e] rounded-md mb-2 cursor-pointer hover:bg-[#1e293b] hover:translate-x-[3px] transition-all last:mb-0"
              >
                <div className={`w-[18px] h-[18px] border-2 border-[#10b981] rounded flex items-center justify-center mr-3 flex-shrink-0 transition-all ${
                  completedChecks.has(`${currentTask.id}-check-${checkIndex}`) ? 'bg-[#10b981]' : ''
                }`}>
                  {completedChecks.has(`${currentTask.id}-check-${checkIndex}`) && (
                    <span className="text-[#1a1f2e] text-xs font-bold">✓</span>
                  )}
                </div>
                <span className="text-[#cbd5e1] text-sm leading-[1.5]">{check}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer Navigation */}
      <div className="flex items-center justify-between px-8 py-5 bg-[#1a1f2e] border-t border-[#2d3748]">
        <div className="text-[#94a3b8] text-sm">
          Progress: Task {currentTaskIndex + 1} of {tasks.length}
        </div>
        
        {/* Progress Bar */}
        <div className="flex-1 mx-[40px]">
          <div className="h-[6px] bg-[#232937] rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-[#10b981] to-[#059669] rounded-full transition-all duration-300 ease-out"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>
        
        {/* Navigation Buttons */}
        <div className="flex space-x-3">
          <button
            onClick={handlePrevious}
            disabled={currentTaskIndex === 0}
            className={`px-5 py-2 rounded-lg font-medium text-sm transition-all ${
              currentTaskIndex === 0 
                ? 'bg-[#232937] text-[#475569] cursor-not-allowed' 
                : 'bg-[#2d3748] text-[#e2e8f0] hover:bg-[#374151]'
            }`}
          >
            Previous
          </button>
          <button
            onClick={handleNext}
            disabled={currentTaskIndex === tasks.length - 1}
            className={`px-5 py-2 rounded-lg font-medium text-sm transition-all ${
              currentTaskIndex === tasks.length - 1
                ? 'bg-[#232937] text-[#475569] cursor-not-allowed'
                : 'bg-[#10b981] text-[#1a1f2e] hover:bg-[#059669]'
            }`}
          >
            Next Task
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReactNodeStackBreakdownInstructions;
